import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, LinearProgress } from '@mui/material';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';

import BiGauge from '../../components/biGauge';
import BiMetric from '../../components/biMetric';
import MonthRangePicker from '../../components/monthRangePicker';
import PageHeader from '../../components/pageHeader';
import { PageIntro } from '../../components/ui';
import { formatMonthParam, getCurrentMonthStart, getMonthsAgoStart } from '../../utils/dateUtils';
import { formatInteger, formatPercentRatio, ratio } from '../../utils/numberUtils';
import { formatCurrency } from '../../utils/stringFormat';
import { buildDashboardAluguelGauges, fetchDashboardAluguel } from './dashboardAluguelData';
import '../css/biAluguelAccess.css';
import '../css/biInadimplencia.css';
import '../css/biDashboard.css';

const minMonth = new Date(2025, 0, 1);
const getDefaultMonth = () => getMonthsAgoStart(1);

const DashboardAluguel = () => {
    const [dataInicio, setDataInicio] = useState(getDefaultMonth);
    const [dataFim, setDataFim] = useState(getDefaultMonth);
    const [dashboard, setDashboard] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const loadDashboard = useCallback(async (startDate, endDate) => {
        setIsLoading(true);
        const periodDashboard = await fetchDashboardAluguel({
            dataInicio: formatMonthParam(startDate),
            dataFim: formatMonthParam(endDate)
        });
        setDashboard(periodDashboard);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        loadDashboard(dataInicio, dataFim);
    }, [dataFim, dataInicio, loadDashboard]);

    const periodQuery = `?data_inicio=${formatMonthParam(dataInicio)}&data_fim=${formatMonthParam(dataFim)}`;
    const gauges = useMemo(() => (
        dashboard ? buildDashboardAluguelGauges(dashboard, periodQuery) : []
    ), [dashboard, periodQuery]);

    const handleDateRangeApply = (startDate, endDate) => {
        setDataInicio(startDate);
        setDataFim(endDate);
    };

    return (
        <Box className='mainBox bi-aluguel-access-page bi-dashboard-page'>
            <PageHeader>BI Aluguel</PageHeader>

            <PageIntro eyebrow='BI Aluguel' title='Visão geral do aluguel'>
                Metas de cancelamento, atraso, satisfação e conclusão de trocas no período escolhido.
            </PageIntro>

            <section className='bi-aluguel-access-shell'>
                {isLoading && <LinearProgress className='bi-aluguel-access-loading' />}

                <div className='bi-aluguel-access-filters bi-ge-indications-filters bi-ge-inadimplencia-period-filter bi-dashboard-period-filter'>
                    <MonthRangePicker
                        startDate={dataInicio}
                        endDate={dataFim}
                        minDate={minMonth}
                        maxDate={getCurrentMonthStart()}
                        disabled={isLoading}
                        onApply={handleDateRangeApply}
                    />
                </div>

                {dashboard && (
                    <>
                        <div className='bi-dashboard-gauges'>
                            {gauges.map(({ key, ...gauge }) => <BiGauge key={key} {...gauge} />)}
                        </div>

                        <div className='bi-aluguel-access-metrics bi-dashboard-metrics'>
                            <BiMetric
                                icon={PaidRoundedIcon}
                                label='Faturamento'
                                value={formatCurrency(dashboard.boletos.total_faturado)}
                                subtitle={`${formatInteger(dashboard.boletos.quantidade_emitidos)} boletos emitidos`}
                                tone='amber'
                            />
                            <BiMetric
                                icon={BoltRoundedIcon}
                                label='Compensação'
                                value={`${formatInteger(dashboard.compensacao.kwh_compensado)} kWh`}
                                subtitle={`${formatPercentRatio(ratio(dashboard.compensacao.kwh_compensado, dashboard.compensacao.kwh_contratado))} do contratado`}
                                tone='blue'
                            />
                            <BiMetric
                                icon={HomeWorkRoundedIcon}
                                label='Base ativa'
                                value={`${formatInteger(dashboard.base.quantidade_unidades)} unidades`}
                                subtitle={`${formatInteger(dashboard.base.quantidade_clientes)} clientes · ${formatInteger(dashboard.base.kwh_contratado)} kWh contratados`}
                                tone='green'
                            />
                        </div>
                    </>
                )}
            </section>
        </Box>
    );
};

export default DashboardAluguel;
